import React from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '#/shared/components/Button';
import { Card } from '#/shared/components/Card';
import { FormControlRadioGroup } from '#/shared/components/FormControl';
import { RadioFieldProps } from '#/shared/components/FormField/RadioField';
import { useFeatureFlag } from '#/shared/hooks/useFeatureFlag';
import { usePaidBreakExceptionStore } from '#/shared/stores/paidBreakExceptionStore';
import { useTimesheetActionViewStore } from '#/shared/stores/timesheetActionViewStore';
import { selectTimesheetIsVerified, useTimesheetStore } from '#/shared/stores/timesheetStore';
import { FeatureFlagAttributes } from '#/shared/types/FeatureFlag';

type ActionsFormValues = {
  actions?: 'approve' | 'verify' | 'forward';
};

export function TimesheetActionsSelection() {
  const clientId = useTimesheetStore(state => state.timesheet?.clientId);
  const featureFlagAttributes: FeatureFlagAttributes = clientId ? { clientId } : {};
  const timesheet = useTimesheetStore(state => state.timesheet);
  const timesheetIsVerified = useTimesheetStore(selectTimesheetIsVerified);
  const doctorName = timesheet?.doctorName;
  const setView = useTimesheetActionViewStore(state => state.setView);
  const lastSelectedAction = useTimesheetActionViewStore(state => state.lastSelectedAction);
  const resetExceptionErrors = usePaidBreakExceptionStore(state => state.resetExceptionErrors);

  const { isEnabled: isVerifyTimesheetEnabled } = useFeatureFlag(
    'PPL-2240-verify-timesheet-action',
    featureFlagAttributes,
  );

  const { control, handleSubmit } = useForm<ActionsFormValues>({
    defaultValues: {
      actions: lastSelectedAction ?? undefined,
    },
  });

  const onSubmit = ({ actions }: ActionsFormValues) => {
    if (actions) {
      if (actions !== 'approve') {
        resetExceptionErrors();
      }
      setView(actions);
    }
  };

  const radioGroupOptions = React.useMemo(() => {
    const options: RadioFieldProps[] = [
      { value: 'approve', label: 'Approve timesheet' },
      {
        value: 'forward',
        label: 'Forward approval request',
        description: "Use if you're not the right approver or verifier",
      },
    ];

    if (isVerifyTimesheetEnabled && !timesheetIsVerified) {
      options.push({
        value: 'verify',
        label: 'Verify and send timesheet to approver',
        description: 'Use if the timesheet needs to be verified against the roster before approval',
      });
    }
    return options;
  }, [isVerifyTimesheetEnabled, timesheetIsVerified]);

  return (
    <Card className="max-tablet-portrait:py-32 max-tablet-portrait:pl-20 max-tablet-portrait:pr-20 max-tablet-portrait:border-0 max-tablet-portrait:rounded-none max-tablet-portrait:border-y-1 max-tablet-portrait:bg-primary-surface-l1">
      <h2 id="timesheet-actions-heading" className="text-24 font-600 mb-8">
        Timesheet actions
      </h2>

      {doctorName && <p>If the timesheet requires changes, contact {doctorName}.</p>}

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="pt-24">
          <FormControlRadioGroup
            name={'actions'}
            control={control}
            errorLeadingIconName={'error'}
            aria-labelledby="timesheet-actions-heading"
            rules={{
              required: 'Choose an option to continue',
            }}
            options={radioGroupOptions}
          />
        </div>
        <div className="pt-24">
          <Button type="submit" className="w-full">
            Next
          </Button>
        </div>
      </form>
    </Card>
  );
}
